import { Dialog, DialogContent, DialogContentText, IconButton, TableBody } from "@material-ui/core";
import { Fullscreen, FullscreenExit } from "@material-ui/icons";
import React from "react";
import styled from "styled-components";
import { ICompanyEnvironmentIntegration } from "../../../../models/ICompanyEnvironment";
import IIntegrationRepository from "../../../../services/IIntegrationRepository";
import Services from "../../../../services/Services";
import snackbarService from "../../../../services/ui-messaging/SnackbarService";
import WithLoadingSpinners from "../../../hoc/WithLoadingSpinner";
import { ObjectTable } from "../../style/IntegrationStyledComponents";
import FullScreenDialogTitle from "../shared/FullScreenDialogTitle";
import ConfirmSecretDialog from "./ConfirmSecretDialog";
import { getEnvironmentAccessRow } from "./EnvironmentAccessRowData";
import { getEnvironmentRow } from "./EnvironmentRowData";
import ShowSecretDialog from "./ShowSecretDialog";
import { getEnvironmentHeader } from "./TableHeaders";

interface IQmlativIntegrationAccessDialog {
  token: string;
  integrationID: number;
  showDialog: boolean;
  onClose: () => void;
}

const WarningDialogContentText = styled(DialogContentText)`
  color: red;
  text-align: center;
`;

export interface ISecret {
  envName: string;
  envKey: string;
  secret: string;
}

export const emptySecret: ISecret = {
  envName: "Unknown",
  envKey: "Unknown",
  secret: "Unknown",
};

const QmlativIntegrationAccessDialog = (props: IQmlativIntegrationAccessDialog) => {
  const { token, integrationID, showDialog, onClose } = props;
  const [showSecret, setShowSecret] = React.useState<boolean>(false);
  const [secret, setSecret] = React.useState<ISecret>(emptySecret);
  const [envAccesses, setEnvAccesses] = React.useState<ICompanyEnvironmentIntegration[]>([]);
  const [accessesOpen, setAccessesOpen] = React.useState<Map<ICompanyEnvironmentIntegration, boolean>>(new Map<ICompanyEnvironmentIntegration, boolean>());
  const [showConfirm, setShowConfirm] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);

  const expandAll = () => applyAll(true);
  const collapseAll = () => applyAll(false);

  let areAllExpanded = Array.from(accessesOpen.values()).reduce((f1, f2) => f1 && f2, true);
  let areAllCollapsed = !Array.from(accessesOpen.values()).reduce((f1, f2) => f1 || f2, false);

  const applyAll = (isOpen: boolean) => {
    let allAccessesApplied = new Map<ICompanyEnvironmentIntegration, boolean>();
    if (envAccesses) {
      envAccesses.forEach(o => allAccessesApplied.set(o, isOpen));
    }

    setAccessesOpen(allAccessesApplied);
  };

  const toggleAccessesOpen = (obj: ICompanyEnvironmentIntegration) => () => {
    let prevValue: boolean = accessesOpen.get(obj) || false;

    setAccessesOpen(prev => new Map(prev).set(obj, !prevValue));
  };

  const [updateAccessName, setUpdateAccessName] = React.useState<string>("");
  const [updateAccessKey, setUpdateAccessKey] = React.useState<string>("");
  const [updateAccessID, setUpdateAccessID] = React.useState<number>(0);

  const confirmUpdate = (accessId: number, accessName: string, accessKey: string) => {
    setUpdateAccessID(accessId);
    setUpdateAccessName(accessName);
    setUpdateAccessKey(accessKey);
    setShowConfirm(true);
  };

  const processUpdate = async () => {
    await getAPISecret(updateAccessID, updateAccessName, updateAccessKey);
  };

  const getAPISecret = async (accessId: number, accessName: string, accessKey: string) => {
    setIsLoading(true);
    try {
      let intRepo: IIntegrationRepository = await new Services().IntegrationRepository();

      let secretResp: string = await intRepo.setSecretHash(token, integrationID, accessId);

      if (secretResp && secretResp !== "") {
        let newSecret: ISecret = {
          envKey: accessKey,
          envName: accessName,
          secret: secretResp,
        };
        setSecret(newSecret);
        setShowSecret(true);
      } else {
        throw new Error(`Could not set secret for '${accessName}'`);
      }
    } catch (err) {
      snackbarService.error(err);
    }
    setIsLoading(false);
  };

  React.useEffect(() => {
    async function getQmlativAccesses() {
      let intRepo: IIntegrationRepository = await new Services().IntegrationRepository();
      let envs: ICompanyEnvironmentIntegration[] = await intRepo.getQmlativEnvironmentsWithIntegrationAccesses(token, integrationID);
      setEnvAccesses(envs);
    }

    getQmlativAccesses();
    setIsLoading(false);
  }, []);

  React.useEffect(() => {
    let initFieldsOpen = new Map<ICompanyEnvironmentIntegration, boolean>();
    if (envAccesses) {
      envAccesses.forEach(o => initFieldsOpen.set(o, false));
    }
    setAccessesOpen(initFieldsOpen);
  }, [envAccesses]);

  return (
    <>
      <ShowSecretDialog showSecret={showSecret} setShowSecret={setShowSecret} setSecret={setSecret} secret={secret} />
      <ConfirmSecretDialog showDialog={showConfirm} setShowDialog={setShowConfirm} processUpdate={processUpdate} />
      <Dialog open={showDialog} onClose={onClose} area-labelledby="qmlativ-access-dialog-title" fullScreen>
        <FullScreenDialogTitle title="Qmlativ Access" onClose={onClose} />
        <DialogContent>
          <WarningDialogContentText>
            The accesses shown below are from the respective Qmlativ sites where the integration has been granted access. For any questions regarding these
            connections or if you believe a site is missing from this list, reach out to the appropriate site contact for more information.
          </WarningDialogContentText>
          <WarningDialogContentText>Skyward DOES NOT have the ability to change these access records.</WarningDialogContentText>
          <IconButton aria-label="expand all" size="small" onClick={expandAll} disabled={areAllExpanded}>
            <Fullscreen />
            Expand All
          </IconButton>
          <IconButton aria-label="collapse all" size="small" onClick={collapseAll} disabled={areAllCollapsed}>
            <FullscreenExit />
            Collapse All
          </IconButton>
          <WithLoadingSpinners isLoading={isLoading} message="Getting data...">
            <ObjectTable>
              {getEnvironmentHeader()}
              <TableBody>
                {envAccesses &&
                  envAccesses.map((ea, index) => {
                    const objectFieldsOpen: boolean | undefined = accessesOpen.get(ea) || false;
                    return (
                      <React.Fragment key={index}>
                        {getEnvironmentRow({ ea, objectFieldsOpen, toggleAccessesOpen })}
                        {getEnvironmentAccessRow({ ea, objectFieldsOpen, index, confirmUpdate, getAPISecret })}
                      </React.Fragment>
                    );
                  })}
              </TableBody>
            </ObjectTable>
          </WithLoadingSpinners>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default QmlativIntegrationAccessDialog;
